import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import {
  AuthError,
  confirmPasswordReset,
  signInWithCustomToken,
  verifyPasswordResetCode,
} from 'firebase/auth';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { getFirebaseAuth } from '../config/firebase';
import { ApiClient, ApiError } from '../services/api';
import { PASSWORD_RULES, isPasswordValid } from '../utils/passwordPolicy';

type ActionState =
  | { phase: 'verifying' }
  | { phase: 'invalid'; message: string }
  | { phase: 'form'; email: string }
  | { phase: 'success'; email: string }
  | { phase: 'expired' }
  | { phase: 'resent' };

function getActionErrorMessage(error: AuthError): string {
  switch (error.code) {
    case 'auth/expired-action-code':
      return 'This password reset link has expired. Please request a new one from the sign-in page.';
    case 'auth/invalid-action-code':
      return 'This password reset link is invalid or has already been used. Please request a new one from the sign-in page.';
    case 'auth/user-disabled':
      return 'This account has been disabled. Please contact support.';
    case 'auth/user-not-found':
      return 'No account found for this link. Please contact support.';
    case 'auth/weak-password':
      return 'The password does not meet the security requirements.';
    default:
      return error.message || 'Something went wrong. Please request a new password reset link.';
  }
}

const CLAIM_USED_MESSAGE = 'This account has already been activated. Please sign in.';
const CLAIM_INVALID_MESSAGE =
  'This activation link is invalid. Please use the link from your email, or contact your administrator for a new invitation.';

/**
 * Sophia-branded Firebase auth action handler.
 *
 * Modes:
 * - `resetPassword` (SOPHIA-34869): Firebase password reset links arrive as
 *   `/auth/action?mode=resetPassword&oobCode=...`. The Firebase email
 *   template's action URL must point at `<portal-origin>/auth/action`.
 * - `claimAccount` (SOPHIA-34868): invitation emails link to
 *   `/auth/action?mode=claimAccount&token=...`. The token is an HMAC-signed,
 *   48-hour, single-use claim token resolved against the portal backend.
 *   Successful claims sign the user in automatically via a Firebase custom
 *   token.
 *
 * Renders inside the app's ThemeProvider, so Sophia branding (Jost font,
 * purple primary, button shapes) applies automatically.
 */
export default function AuthAction(): React.ReactElement {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');
  const claimToken = searchParams.get('token');
  const isClaim = mode === 'claimAccount';

  const [state, setState] = useState<ActionState>({ phase: 'verifying' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const api = useMemo(() => new ApiClient(null), []);

  useEffect(() => {
    let cancelled = false;

    if (mode === 'resetPassword' && oobCode) {
      verifyPasswordResetCode(getFirebaseAuth(), oobCode)
        .then((email) => {
          if (!cancelled) setState({ phase: 'form', email });
        })
        .catch((err: AuthError) => {
          if (!cancelled) setState({ phase: 'invalid', message: getActionErrorMessage(err) });
        });
    } else if (mode === 'claimAccount' && claimToken) {
      api
        .resolveAccountClaim(claimToken)
        .then((resolved) => {
          if (cancelled) return;
          switch (resolved.state) {
            case 'valid':
              setState({ phase: 'form', email: resolved.email ?? '' });
              break;
            case 'expired':
              setState({ phase: 'expired' });
              break;
            case 'used':
              setState({ phase: 'invalid', message: CLAIM_USED_MESSAGE });
              break;
            default:
              setState({ phase: 'invalid', message: CLAIM_INVALID_MESSAGE });
          }
        })
        .catch(() => {
          if (!cancelled) {
            setState({
              phase: 'invalid',
              message:
                'Something went wrong while checking your activation link. Please try again.',
            });
          }
        });
    } else {
      setState({
        phase: 'invalid',
        message:
          'This link is invalid or incomplete. Please use the link from your email, or request a new one from the sign-in page.',
      });
    }

    return () => {
      cancelled = true;
    };
  }, [mode, oobCode, claimToken, api]);

  const passwordsMatch = password === confirm;
  const canSubmit = isPasswordValid(password) && passwordsMatch && !submitting;

  const ruleStatus = useMemo(
    () => PASSWORD_RULES.map((rule) => ({ ...rule, passed: rule.test(password) })),
    [password]
  );

  const handleResetSubmit = async (): Promise<void> => {
    if (state.phase !== 'form' || !oobCode) return;
    try {
      await confirmPasswordReset(getFirebaseAuth(), oobCode, password);
      setState({ phase: 'success', email: state.email });
    } catch (err) {
      setError(getActionErrorMessage(err as AuthError));
    }
  };

  const handleClaimSubmit = async (): Promise<void> => {
    if (state.phase !== 'form' || !claimToken) return;
    try {
      const { customToken } = await api.claimAccount(claimToken, password);
      if (!customToken) {
        // Claim succeeded but the backend couldn't mint a sign-in token
        setState({ phase: 'success', email: state.email });
        return;
      }
      try {
        await signInWithCustomToken(getFirebaseAuth(), customToken);
        navigate('/dashboard', { replace: true });
      } catch {
        // Auto sign-in failed — the password is set, so let them sign in manually
        setState({ phase: 'success', email: state.email });
      }
    } catch (err) {
      if (err instanceof ApiError && err.status === 410) {
        setState({ phase: 'expired' });
      } else if (err instanceof ApiError && err.status === 409) {
        setState({ phase: 'invalid', message: CLAIM_USED_MESSAGE });
      } else {
        setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (state.phase !== 'form' || !canSubmit) return;

    setSubmitting(true);
    setError('');
    try {
      if (isClaim) {
        await handleClaimSubmit();
      } else {
        await handleResetSubmit();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (): Promise<void> => {
    if (!claimToken) return;
    setSubmitting(true);
    setError('');
    try {
      await api.resendAccountClaim(claimToken);
      setState({ phase: 'resent' });
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        setState({ phase: 'invalid', message: CLAIM_USED_MESSAGE });
      } else {
        setError(
          err instanceof Error ? err.message : 'Unable to send a new link. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          py: 4,
        }}
      >
        <Card elevation={0} sx={{ borderRadius: '4px' }}>
          <CardContent sx={{ p: 4 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <Box
                component="img"
                src="/sophia-logo.svg"
                alt="Sophia Learning"
                sx={{ height: 60, mr: 2 }}
              />
              <div>
                <Typography variant="h5" component="h1" fontWeight="bold">
                  Sophia Learning Partner Portal
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isClaim ? 'Set up your account' : 'Reset your password'}
                </Typography>
              </div>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {state.phase === 'verifying' && (
              <Typography color="text.secondary">
                {isClaim ? 'Verifying your activation link…' : 'Verifying your reset link…'}
              </Typography>
            )}

            {state.phase === 'invalid' && (
              <>
                <Alert severity="error" sx={{ mb: 3 }}>
                  {state.message}
                </Alert>
                <Button component={RouterLink} to="/login" variant="contained" fullWidth>
                  Back to Sign In
                </Button>
              </>
            )}

            {state.phase === 'expired' && (
              <>
                <Alert severity="warning" sx={{ mb: 3 }}>
                  This activation link has expired — links are valid for 48 hours. Request a new one
                  below and we&apos;ll email it to you.
                </Alert>
                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}
                <Button
                  variant="contained"
                  fullWidth
                  size="large"
                  onClick={handleResend}
                  disabled={submitting}
                >
                  {submitting ? 'Sending…' : 'Send Email with New Link'}
                </Button>
              </>
            )}

            {state.phase === 'resent' && (
              <Alert severity="success">
                A new activation link has been sent to your email. The link is valid for 48 hours.
                You can close this page.
              </Alert>
            )}

            {state.phase === 'form' && (
              <>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {isClaim ? (
                    <>
                      Welcome! Create a password for <strong>{state.email}</strong> to finish
                      setting up your account.
                    </>
                  ) : (
                    <>
                      Choose a new password for <strong>{state.email}</strong>.
                    </>
                  )}
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit}>
                  <TextField
                    label={isClaim ? 'Password' : 'New Password'}
                    type="password"
                    fullWidth
                    required
                    autoComplete="new-password"
                    autoFocus
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    sx={{ mb: 2 }}
                    disabled={submitting}
                  />
                  <TextField
                    label={isClaim ? 'Confirm Password' : 'Confirm New Password'}
                    type="password"
                    fullWidth
                    required
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    error={confirm.length > 0 && !passwordsMatch}
                    helperText={
                      confirm.length > 0 && !passwordsMatch ? 'Passwords do not match' : ' '
                    }
                    sx={{ mb: 1 }}
                    disabled={submitting}
                  />

                  <List dense sx={{ mb: 2 }}>
                    {ruleStatus.map((rule) => (
                      <ListItem key={rule.id} disablePadding>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          {rule.passed ? (
                            <CheckCircleOutlineIcon color="success" fontSize="small" />
                          ) : (
                            <RadioButtonUncheckedIcon color="disabled" fontSize="small" />
                          )}
                        </ListItemIcon>
                        <ListItemText
                          primary={rule.label}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    size="large"
                    disabled={!canSubmit}
                  >
                    {submitting
                      ? isClaim
                        ? 'Setting up your account…'
                        : 'Saving…'
                      : isClaim
                        ? 'Set Password & Sign In'
                        : 'Reset Password'}
                  </Button>
                </form>
              </>
            )}

            {state.phase === 'success' && (
              <>
                <Alert severity="success" sx={{ mb: 3 }}>
                  {isClaim
                    ? 'Your account is ready. You can now sign in with your new password.'
                    : 'Your password has been reset. You can now sign in with your new password.'}
                </Alert>
                <Button component={RouterLink} to="/login" variant="contained" fullWidth>
                  Back to Sign In
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Compliance Footer */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" color="text.secondary">
            This system processes student data in compliance with FERPA, COPPA, and SOC 2 Type II
            standards.
            <br />
            Unauthorized access is prohibited.
          </Typography>
        </Box>
      </Box>
    </Container>
  );
}
